import { z } from "zod";
import {
  categoryLabelsSchema,
  groupCategorySchema,
  groupCategoryTypeSchema,
  refineCategoryLabelsByType,
} from "./groups.js";

/**
 * Shared DTO cho Admin Master Data & Dictionaries (US-AE15.F1-0090 · @UC9140 read / @UC9141 write).
 * Một nguồn sự thật cho api (S01 — map hằng/DB → DTO) và admin web (S03 — render engine list), tránh
 * drift shape ở 2 nơi (DRY). Mirror style admin-user-directory.ts.
 *
 * BẢO MẬT / INVARIANTS:
 * - C3: mọi schema `.strict()` — KHÔNG khai báo secret/PII thô; field lạ bị reject tại biên.
 * - Enum `status`/`domain`/`termType` = literal-union (fail-closed giá trị lạ).
 * - KHÔNG field tiền (banks lưu BIN/metadata, dictionary metadata thuần) → KHÔNG dính B1 float.
 * - Số đếm (`hits`/`count`) = integer non-negative.
 * - READ-ONLY registry (income/expense/accounts/config-enums/error-codes/banks) KHÔNG có create/patch
 *   schema (write chặn ở tầng contract — không cung cấp shape ghi).
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size cho 3 dictionary DB-backed. Default (20) do backend áp khi vắng. */
export const ADMIN_DICTIONARY_MAX_LIMIT = 50;

/** Trạng thái enable/disable (không hard-delete). Dùng chung mọi dictionary. */
export const dictionaryStatusSchema = z.enum(["enabled", "paused"]);
export type DictionaryStatus = z.infer<typeof dictionaryStatusSchema>;

/** Miền áp dụng của blocklist/reason-code (moderation/risk/dispute/abuse). */
export const dictionaryDomainSchema = z.enum(["mod_dom", "risk_dom", "dispute_dom", "abuse_dom"]);
export type DictionaryDomain = z.infer<typeof dictionaryDomainSchema>;

/** Kiểu term của blocklist (từ khoá tĩnh hoặc regex). */
export const blocklistTermTypeSchema = z.enum(["keyword", "regex"]);
export type BlocklistTermType = z.infer<typeof blocklistTermTypeSchema>;

/** Query list DB-backed (cursor + limit). Vắng cursor ⇒ trang đầu. */
export const adminDictionaryQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_DICTIONARY_MAX_LIMIT).optional(),
});
export type AdminDictionaryQuery = z.infer<typeof adminDictionaryQuerySchema>;

/** PageInfo dùng chung (cursor opaque, hasNext). */
const pageInfoSchema = z.object({
  hasNext: z.boolean(),
  nextCursor: z.string().nullable(),
});

/**
 * Envelope list DB-backed generic `{ data: item[], pageInfo }`. Helper để mỗi dictionary bọc item
 * schema riêng mà không nhân bản shape envelope (DRY).
 */
export function dictionaryResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), pageInfo: pageInfoSchema });
}

// ============================================================
// A. READ-ONLY file/code-based registries (@UC9140) — KHÔNG create/patch schema
// ============================================================

/** income-types: mirror resources.ts `income_types` (code/label/ledger/kind/status). */
export const incomeTypeItemSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    ledger: z.string(),
    kind: z.string(),
    status: dictionaryStatusSchema,
  })
  .strict();
export type IncomeTypeItem = z.infer<typeof incomeTypeItemSchema>;

/** expense-categories: mirror resources.ts `expense_categories` (code/label/hint/status). */
export const expenseCategoryItemSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    hint: z.string(),
    status: dictionaryStatusSchema,
  })
  .strict();
export type ExpenseCategoryItem = z.infer<typeof expenseCategoryItemSchema>;

/** chart-of-accounts: mirror resources.ts `chart_of_accounts` (code/label/side/note). READ-ONLY bất biến. */
export const accountItemSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    side: z.enum(["debit", "credit"]),
    note: z.string(),
  })
  .strict();
export type AccountItem = z.infer<typeof accountItemSchema>;

/** config-enums: mirror resources.ts `enum_registry` (code/values/def/overridable). Read từ SystemSetting. */
export const configEnumItemSchema = z
  .object({
    code: z.string(),
    values: z.string(), // allowed values ghép chuỗi hiển thị
    def: z.string(), // default value hiển thị
    overridable: z.enum(["active", "inactive"]), // groupOverridable → active|inactive
  })
  .strict();
export type ConfigEnumItem = z.infer<typeof configEnumItemSchema>;

/**
 * error-codes: mirror resources.ts `error_catalog` (code/http/vi/en/status). `en` có thể rỗng khi
 * ERROR_CATALOG chưa có bản dịch (i18n backfill) — contract cho phép chuỗi rỗng, KHÔNG bịa dịch.
 */
export const errorCodeItemSchema = z
  .object({
    code: z.string(),
    http: z.string(), // httpStatus dạng chuỗi (cột mono UI)
    vi: z.string(),
    en: z.string(), // "" khi chưa có bản dịch (i18n TODO)
    status: z.enum(["active", "inactive"]),
  })
  .strict();
export type ErrorCodeItem = z.infer<typeof errorCodeItemSchema>;

/** banks: mirror resources.ts `banks` (bin/code/label/napas/status). Read từ NAPAS_BANK_BINS. */
export const bankItemSchema = z
  .object({
    bin: z.string(),
    code: z.string(),
    label: z.string(),
    napas: z.enum(["active", "inactive"]),
    status: dictionaryStatusSchema,
  })
  .strict();
export type BankItem = z.infer<typeof bankItemSchema>;

// ============================================================
// B. DB-backed dictionaries (@UC9140 read + @UC9141 write) — có create/patch
// ============================================================

/** blocklist row (list item): mirror resources.ts `blocklist` + id/createdAt. */
export const blocklistItemSchema = z
  .object({
    id: z.string(),
    term: z.string(),
    ttype: blocklistTermTypeSchema, // termType → ttype (cột UI)
    domain: dictionaryDomainSchema,
    hits: z.number().int().min(0),
    status: dictionaryStatusSchema,
    createdAt: z.string(),
  })
  .strict();
export type BlocklistItem = z.infer<typeof blocklistItemSchema>;

/** Tạo blocklist (write @UC9141). term/termType/domain bắt buộc; status optional (default enabled). */
export const createBlocklistSchema = z
  .object({
    term: z.string().min(1).max(500),
    termType: blocklistTermTypeSchema,
    domain: dictionaryDomainSchema,
    status: dictionaryStatusSchema.optional(),
  })
  .strict();
export type CreateBlocklistBody = z.infer<typeof createBlocklistSchema>;

/** Sửa blocklist (write @UC9141). Mọi field optional; ≥1 field (refine); strict. */
export const patchBlocklistSchema = z
  .object({
    term: z.string().min(1).max(500).optional(),
    termType: blocklistTermTypeSchema.optional(),
    domain: dictionaryDomainSchema.optional(),
    status: dictionaryStatusSchema.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: "Cần ít nhất một trường để cập nhật" });
export type PatchBlocklistBody = z.infer<typeof patchBlocklistSchema>;

/** reason-code row (list item): mirror resources.ts `reason_codes` + id/createdAt. */
export const reasonCodeItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    label: z.string(),
    domain: dictionaryDomainSchema,
    status: dictionaryStatusSchema,
    createdAt: z.string(),
  })
  .strict();
export type ReasonCodeItem = z.infer<typeof reasonCodeItemSchema>;

/** Tạo reason-code (write @UC9141). code/label/domain bắt buộc; status optional. */
export const createReasonCodeSchema = z
  .object({
    code: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    domain: dictionaryDomainSchema,
    status: dictionaryStatusSchema.optional(),
  })
  .strict();
export type CreateReasonCodeBody = z.infer<typeof createReasonCodeSchema>;

/** Sửa reason-code (write @UC9141). Mọi field optional; ≥1 field; strict. code KHÔNG sửa (unique key). */
export const patchReasonCodeSchema = z
  .object({
    label: z.string().min(1).max(200).optional(),
    domain: dictionaryDomainSchema.optional(),
    status: dictionaryStatusSchema.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: "Cần ít nhất một trường để cập nhật" });
export type PatchReasonCodeBody = z.infer<typeof patchReasonCodeSchema>;

/** referral-channel row (list item): mirror resources.ts `referral_channels` + id/createdAt. */
export const referralChannelItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    label: z.string(),
    tracked: z.enum(["active", "inactive"]), // tracked boolean → active|inactive (cột UI)
    count: z.number().int().min(0), // usageCount → count (cột UI)
    status: dictionaryStatusSchema,
    createdAt: z.string(),
  })
  .strict();
export type ReferralChannelItem = z.infer<typeof referralChannelItemSchema>;

/** Tạo referral-channel (write @UC9141). code/label bắt buộc; tracked/status optional. */
export const createReferralChannelSchema = z
  .object({
    code: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    tracked: z.boolean().optional(),
    status: dictionaryStatusSchema.optional(),
  })
  .strict();
export type CreateReferralChannelBody = z.infer<typeof createReferralChannelSchema>;

/**
 * US-E4.F1-0388-S01/S02 (@UC0055) — group-category row (admin list item).
 * Admin THẤY `status` (kể cả `paused` như hometown/other giữ-cho-FK) — khác public item ở
 * `groups.ts` vốn giấu status/id. `type` là LUẬT ánh xạ danh tính gắn cho mọi nhóm tạo bằng
 * category này — xem chú thích `groupCategoryTypeSchema`.
 */
export const groupCategoryItemSchema = z
  .object({
    id: z.string(),
    code: groupCategorySchema,
    type: groupCategoryTypeSchema,
    nameVi: z.string(),
    nameEn: z.string(),
    status: dictionaryStatusSchema,
    createdAt: z.string(),
    /**
     * US-E4.F1-0431-S03 (@UC9678) — gói từ vựng hiển thị của category. VẮNG MẶT khi chưa cấu hình
     * (7 category `standard` cố ý không có); KHÔNG dùng `null` — một trạng thái, MỘT cách biểu
     * diễn (xem `groupCategoryEmbeddedSchema`). Admin phải đọc lại được đúng thứ mình vừa ghi,
     * nếu không thì "sửa nhãn" thành thao tác mù.
     */
    labels: categoryLabelsSchema.optional(),
  })
  .strict();
export type GroupCategoryItem = z.infer<typeof groupCategoryItemSchema>;

/**
 * Tạo group-category (write). code theo shape snake_case (mirror CHECK
 * `group_categories_code_shape_check` ở DB); `type` BẮT BUỘC khai tường minh — không default lặng
 * lẽ, vì đây là luật ánh xạ danh tính cho mọi nhóm sẽ tạo bằng category này. status optional
 * (default `enabled`).
 *
 * US-E4.F1-0431-S03 (@UC9678): nhận thêm `labels`. Ràng buộc `type` ↔ `labels` KHÔNG viết lại ở
 * đây — dùng chung `refineCategoryLabelsByType` với object `category` trong response.
 */
export const createGroupCategorySchema = z
  .object({
    code: groupCategorySchema,
    type: groupCategoryTypeSchema,
    nameVi: z.string().trim().min(1).max(200),
    nameEn: z.string().trim().min(1).max(200),
    status: dictionaryStatusSchema.optional(),
    labels: categoryLabelsSchema.optional(),
  })
  .strict()
  .superRefine(refineCategoryLabelsByType);
export type CreateGroupCategoryBody = z.infer<typeof createGroupCategorySchema>;

/**
 * US-E4.F1-0431-S03 (@UC9678) — SỬA group-category. Trước task này chỉ có `@Get` + `@Post`: gõ
 * nhầm một danh từ của category `parent` là mọi nhóm thuộc loại đó hiện sai từ vựng và **không có
 * đường sửa qua API**, phải vào thẳng DB.
 *
 * 🔴 KHÔNG `code`: khoá nghiệp vụ bất biến, có FK `groups.category` (ON UPDATE RESTRICT). Gửi
 * `code` ⇒ 400 ở biên nhờ `.strict()` — im lặng bỏ qua thì admin tưởng đã đổi tên mã.
 * 🔴 KHÔNG `type`: đổi luật ánh xạ danh tính của category đã có nhóm dùng ⇒ mồ côi
 * MembershipAccount (QĐ giữ từ US-E4.F1-0388). Muốn đổi luật ⇒ tạo category khác.
 *
 * ⚠️ Ràng buộc `type` ↔ `labels` KHÔNG kiểm được ở đây (không có `type` trong body) ⇒ service đối
 * chiếu với row đang có, dùng CHUNG `categoryLabelsTypeViolation`.
 */
export const patchGroupCategorySchema = z
  .object({
    nameVi: z.string().trim().min(1).max(200).optional(),
    nameEn: z.string().trim().min(1).max(200).optional(),
    status: dictionaryStatusSchema.optional(),
    labels: categoryLabelsSchema.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: "Cần ít nhất một trường để cập nhật" });
export type PatchGroupCategoryBody = z.infer<typeof patchGroupCategorySchema>;

/** Sửa referral-channel (write @UC9141). Mọi field optional; ≥1 field; strict. code KHÔNG sửa. */
export const patchReferralChannelSchema = z
  .object({
    label: z.string().min(1).max(200).optional(),
    tracked: z.boolean().optional(),
    status: dictionaryStatusSchema.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: "Cần ít nhất một trường để cập nhật" });
export type PatchReferralChannelBody = z.infer<typeof patchReferralChannelSchema>;

// ============================================================
// Response envelopes DB-backed
// ============================================================
export const blocklistResponseSchema = dictionaryResponseSchema(blocklistItemSchema);
export type BlocklistResponse = z.infer<typeof blocklistResponseSchema>;

export const reasonCodeResponseSchema = dictionaryResponseSchema(reasonCodeItemSchema);
export type ReasonCodeResponse = z.infer<typeof reasonCodeResponseSchema>;

export const referralChannelResponseSchema = dictionaryResponseSchema(referralChannelItemSchema);
export type ReferralChannelResponse = z.infer<typeof referralChannelResponseSchema>;

/** US-E4.F1-0388-S02 (@UC0055): list admin group-categories — cùng envelope {data,pageInfo}. */
export const groupCategoryResponseSchema = dictionaryResponseSchema(groupCategoryItemSchema);
export type GroupCategoryResponse = z.infer<typeof groupCategoryResponseSchema>;
